import { computed } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import type { SignaturesBlock } from '~/models/blocks/signatures-block.model';
import type { SignatureBlock } from '~/models/blocks/signature-block.model';
import { DnDTypes, TBlock } from '~/types/builder.types';
import type { ContractHTMLBlock } from '~/models/blocks/contract-html-block.model';
import { IContractBlockResponse } from '~/types/server.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';

type ContractBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    Pick<IContractBlockResponse, 'type'>;

export class ContractBlock extends ContainerableBlock {
    type: 'contract_block';

    constructor(
        block: IContractBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    get isDeletable() {
        return true;
    }

    @computed
    get contractHTMLBlock(): ContractHTMLBlock {
        return this.childBlocks.find(
            block => block.type === 'contract_html_block'
        ) as ContractHTMLBlock;
    }

    @computed
    get shouldAddCompositionMenu() {
        return true;
    }

    @computed
    get signaturesBlock() {
        return this.childBlocks.find(
            block => block.type === 'signatures_block'
        ) as SignaturesBlock;
    }

    get isActionBlock() {
        return true;
    }

    get isResponsesActionBlock() {
        return true;
    }

    get displayName() {
        return 'contract';
    }

    @computed
    get isBeforeSelection() {
        const lastPageWithServiceSelection = last(
            this.flow.pagesWithSelectableServices.sort(
                (a, b) => a.order - b.order
            )
        );

        return this.page.order <= lastPageWithServiceSelection?.order;
    }

    @computed
    get isBeforeSchedulerblock() {
        return this.page.order <= this.flow.pageWithScheduler?.order;
    }

    @computed
    get smartFieldsCount() {
        return this.contractHTMLBlock?.fields?.length || 0;
    }

    @computed
    get hasPaymentRecaps() {
        return !!this.contractHTMLBlock?.recaps?.find(
            recap => recap.type === 'payments_recap'
        );
    }

    @computed
    get hasRecaps() {
        return !isEmpty(this.contractHTMLBlock.recaps);
    }

    @computed
    get hasServicesRecaps() {
        return this.contractHTMLBlock?.recaps?.some(
            recap => recap.type === 'services_recap'
        );
    }

    @computed
    get hasCriticalChanges() {
        return (
            this.critical_changes?.length > 0 ||
            this.contractHTMLBlock.critical_changes?.length > 0 ||
            this.signaturesBlock.critical_changes?.length > 0
        );
    }

    @computed
    get hasClientSignatures() {
        return this.signaturesBlock.hasClientSignatures;
    }

    @computed
    get nonVendorSignedSignatures() {
        return this.signaturesBlock.childBlocks.filter(signatureBlock => {
            const typedSignatureBlock = signatureBlock as SignatureBlock;
            const {
                builderUiStore: { selectedFlow },
                userStore: { currUser }
            } = this.rootStore;

            // signee isn't current user
            // and signature is signed
            // and signee is team member or client
            return (
                typedSignatureBlock.connectedVariable &&
                typedSignatureBlock.signature.signee_id !== currUser._id &&
                !!typedSignatureBlock.connectedVariable.value.signature &&
                (selectedFlow.isTeamMember(
                    typedSignatureBlock.signature.signee_id
                ) ||
                    selectedFlow.isFlowClient(
                        typedSignatureBlock.signature.signee_id
                    ))
            );
        }) as SignatureBlock[];
    }

    isDraggable() {
        return true;
    }

    @computed
    get dndType() {
        return DnDTypes.HorizontalBlock;
    }

    dndAcceptedTypes() {
        return DnDTypes.HorizontalBlock;
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    canBeParentOf(block: TBlock) {
        return ['contract_html_block', 'signatures_block'].includes(block.type);
    }

    getDataForServer(): ContractBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'contract_block'
        };
    }

    @computed
    get isCutDisabled() {
        return this.hasClientSignatures;
    }
}
